/**
 * Requests content access from the server.
 * Returns a promise that resolves with the response JSON.
 *
 * @param {string} contentToken The content token to request access for.
 * @return {Promise} Resolves with the parsed JSON response.
 */
function requestContentAccess(contentToken) {
	var language = document.documentElement.lang;
	var url = '/include/ajax/knowledge.php?action=play_content&content_token=' + encodeURIComponent(contentToken) + '&language=' + encodeURIComponent(language);

	return fetch(url)
		.then(function(response) {
			if (!response.ok) {
				console.error('Content access request failed:', response.status, response.statusText);
				return { success: false, error: 'Request failed with status ' + response.status };
			}
			return response.text().then(function(text) {
				try {
					return JSON.parse(text);
				} catch (e) {
					console.error('Content access response (not JSON):', text);
					return { success: false, error: 'Invalid JSON response.' };
				}
			});
		})
		.catch(function(error) {
			console.error('Content access request error:', error);
			return { success: false, error: error.message };
		});
}

/**
 * Handles an access-denied response by redirecting to the access page.
 * Appends the content token as a query parameter so the access and
 * register pages can track what content was requested.
 *
 * @param {string} contentToken The content token that was denied.
 */
function handleAccessDenied(contentToken) {
	var base_url;
	if (typeof window.contentAccessUrl === 'string' && window.contentAccessUrl.length > 0) {
		base_url = window.contentAccessUrl;
	} else {
		base_url = '/' + document.documentElement.lang + '/register';
	}

	if (contentToken) {
		base_url += (base_url.indexOf('?') === -1 ? '?' : '&') + 'content_token=' + encodeURIComponent(contentToken);
	}

	window.location.href = base_url;
}

/**
 * Loads content via the access endpoint and plays video inline.
 * Replaces the thumbnail with a video player on success.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}       event            The click event.
 * @param {HTMLElement} thumbnailElement The thumbnail container element.
 * @param {string}      contentToken     The content token to request.
 */
function playVideo(event, thumbnailElement, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			// Create video element.
			var video = document.createElement('video');
			video.controls = true;
			video.autoplay = true;
			video.style.position = 'absolute';
			video.style.top = '0';
			video.style.left = '0';
			video.style.width = '100%';
			video.style.height = '100%';
			video.style.objectFit = 'cover';
			video.src = data.url;

			// Clear thumbnail and insert video.
			thumbnailElement.innerHTML = '';
			thumbnailElement.appendChild(video);
			thumbnailElement.style.cursor = 'default';
			thumbnailElement.onclick = null;
		} else if (data.access_denied) {
			handleAccessDenied(contentToken);
		}
	});
}

/**
 * Loads content via the access endpoint and starts playback on a page video player.
 * Sets the video source and plays on success.
 * Redirects to the access page if access is denied.
 *
 * @param {HTMLElement} playerElement The video element to play.
 * @param {string}      contentToken  The content token to request.
 */
function playContentVideo(playerElement, contentToken) {
	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			playerElement.src = data.url;
			playerElement.play();

			// Hide play overlay if present.
			var overlay = playerElement.parentElement.querySelector('.content-play-overlay');
			if (overlay) {
				overlay.style.display = 'none';
			}
		} else if (data.access_denied) {
			handleAccessDenied(contentToken);
		}
	});
}

/**
 * Loads content via the access endpoint and opens the download URL.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function downloadContent(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			window.open(data.url, '_blank');
		} else if (data.access_denied) {
			handleAccessDenied(contentToken);
		}
	});
}

/**
 * Navigates to a content viewing page.
 *
 * @param {string} contentUrl The URL of the content to view.
 */
function viewContent(contentUrl) {
	window.location.href = contentUrl;
}

/**
 * Loads content via the access endpoint and opens the PDF viewer modal.
 * Reads the document title from the triggering element's data-pdf-title attribute.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function viewDocument(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	var trigger = event.currentTarget;
	var title = 'Document';
	if (trigger && trigger.getAttribute) {
		var attr_title = trigger.getAttribute('data-pdf-title');
		if (attr_title) {
			title = attr_title;
		}
	}

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			if (window.pdfViewer && typeof window.pdfViewer.openPDF === 'function') {
				window.pdfViewer.openPDF(data.url, title);
			} else {
				console.error('PDF viewer is not initialized.');
			}
		} else if (data.access_denied) {
			handleAccessDenied(contentToken);
		}
	});
}

// Attach to window so they are accessible from inline onclick handlers.
window.downloadContent = downloadContent;
window.playContentVideo = playContentVideo;
window.playVideo = playVideo;
window.viewContent = viewContent;
window.viewDocument = viewDocument;
